import { useEffect, useState, useRef } from 'react';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { AiKeyMissingNotice } from './AiKeyBanner.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';
import { Modal } from './ui';
import AiBusy, { AI_COST } from './AiBusy.jsx';
import { formatDateTime } from '../util/time.js';
import { lazyImport } from '../util/lazyImport.js';
import LanguageSelect from './LanguageSelect.jsx';
import { getPreferredLanguage, setPreferredLanguage } from '../util/languages.js';
import { languageDefault, languageReasonText } from '../util/postingLanguageDefault.js';

// jsPDF is ~360KB minified — lazy-loaded on first PDF click so the
// initial bundle stays light. Cached after first download.

// T025 — Per-recommendation cover letter editor + generator.
// Modal scoped to ONE recommendation. Loads the current cover letter from
// the `rec.coverLetter` prop, lets the operator:
//   - Generate / Regenerate via Claude Sonnet against the job's full
//     description (POST /api/recommendations/:id/cover-letter)
//   - Optionally pass notes for the LLM ("emphasise X", "I'm relocating to Y")
//   - Preview the letter as a typeset sheet, or edit the body in-place (PATCH)
//   - Copy to clipboard / download a justified A4 PDF
//   - Discard the draft (DELETE)
//
// Tight binding to the job: the title bar shows the job + company so the
// operator always knows which posting they're writing for. No way to
// generate a "generic" cover letter from this UI.

// Split a plain-text body into paragraphs on blank lines. Single newlines
// inside a paragraph are preserved by the caller (signature blocks etc.).
function toParagraphs(text) {
  return String(text || '')
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+$/g, ''))
    .filter((p) => p.trim().length > 0);
}

function formatLetterDate(d = new Date()) {
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// Draw one line of body text. When `flush` is false we justify the line by
// distributing the slack evenly between words (classic full-justification);
// the last line of a paragraph and single-word lines stay left-aligned so we
// don't get stretched-out "rivers". jsPDF has no native per-line justify that
// also leaves the last line ragged, so we space the words by hand.
function drawJustifiedLine(doc, line, x, y, maxW, flush) {
  const words = line.split(' ').filter(Boolean);
  if (flush || words.length <= 1) {
    doc.text(line, x, y);
    return;
  }
  const spaceW = doc.getTextWidth(' ');
  const natural = doc.getTextWidth(words.join(' '));
  const gaps = words.length - 1;
  const extra = (maxW - natural) / gaps;
  // Negative slack (line a hair too wide) or an absurd stretch on a short
  // line → fall back to a plain left-aligned line.
  if (extra <= 0 || extra > spaceW * 5) {
    doc.text(line, x, y);
    return;
  }
  let cx = x;
  words.forEach((w, i) => {
    doc.text(w, cx, y);
    cx += doc.getTextWidth(w) + spaceW + (i < gaps ? extra : 0);
  });
}

export default function CoverLetterModal({ rec, onClose, onUpdated }) {
  const job = rec?.job;
  const existing = rec?.coverLetter ?? null;
  const { user } = useAuth();
  const hasAnthropicKey = !!user?.connectors?.anthropicKey;
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [letter, setLetter] = useState(existing);
  const [notes, setNotes] = useState(existing?.notes ?? '');
  // Output language for the generated letter. Restores the language of an
  // existing draft, else the operator's sticky last choice.
  // T469 — the posting's own language outranks the sticky preference.
  const langDefault = languageDefault({
    existingLanguage: existing?.language,
    postingLanguage: rec?.job?.postingLanguage,
  });
  const [language, setLanguage] = useState(langDefault.code);
  const changeLanguage = (code) => {
    setLanguage(code);
    setPreferredLanguage(code);
  };
  const [subject, setSubject] = useState(existing?.subject ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [showNotesInput, setShowNotesInput] = useState(false);
  // 'preview' shows the typeset sheet (default when a letter exists);
  // 'edit' swaps in the subject/body fields.
  const [mode, setMode] = useState('preview');
  const bodyRef = useRef(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false); // T111 — unsaved-edits guard
  // T133 — one-deep undo: the prior letter the server stashed on regenerate.
  const [previous, setPrevious] = useState(rec?.coverLetterPrevious ?? null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Sync local edits → state when a regeneration overwrites the draft
  useEffect(() => {
    if (letter) {
      setSubject(letter.subject ?? '');
      setBody(letter.body ?? '');
      setNotes(letter.notes ?? '');
    }
  }, [letter]);

  const dirty =
    !!letter &&
    (subject !== (letter.subject ?? '') || body !== (letter.body ?? '') || notes !== (letter.notes ?? ''));

  // T111 — closing with unsaved manual edits routes through a confirm.
  const attemptClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose?.();
  };
  // Escape is owned by <Modal> (onClose={attemptClose}, suppressed while a
  // nested ConfirmDialog is open via escapeClosesOnTop).

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/cover-letter`, { notes, language });
      const cl = r.recommendation.coverLetter;
      setLetter(cl);
      setPrevious(r.recommendation.coverLetterPrevious ?? null); // T133 — light the restore chip
      setShowNotesInput(false);
      setMode('preview');
      toast('Cover letter generated', { kind: 'success' });
      onUpdated?.({ coverLetter: cl, coverLetterPrevious: r.recommendation.coverLetterPrevious ?? null });
    } catch (e) {
      const msg =
        e.body?.error === 'anthropic_key_missing'
          ? 'Set your Anthropic API key in Settings → Connectors first.'
          : e.body?.message || e.message;
      toast(msg, { kind: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  // T133 — restore the stashed previous letter (one-deep undo; server clears
  // the buffer so it's a single restore, not a yo-yo).
  const restore = async () => {
    setRestoring(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/cover-letter/restore-previous`);
      const cl = r.recommendation.coverLetter;
      setLetter(cl); // the [letter] effect re-syncs subject/body/notes
      setPrevious(null);
      setMode('preview');
      setConfirmRestore(false);
      toast('Restored previous version', { kind: 'success' });
      onUpdated?.({ coverLetter: cl, coverLetterPrevious: null });
    } catch (e) {
      toast(e.body?.message || e.message || 'Restore failed', { kind: 'error' });
    } finally {
      setRestoring(false);
    }
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/recommendations/${rec.id}/cover-letter`, { subject, body, notes });
      const cl = r.recommendation.coverLetter;
      setLetter(cl);
      setMode('preview');
      toast('Edits saved', { kind: 'success' });
      onUpdated?.({ coverLetter: cl, coverLetterPrevious: previous ?? null }); // T133 — a manual edit doesn't touch the undo buffer
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    if (!letter) return;
    try {
      await api.delete(`/recommendations/${rec.id}/cover-letter`);
      setLetter(null);
      setSubject('');
      setBody('');
      setNotes('');
      setPrevious(null); // T133 — discarding the draft discards its undo buffer
      toast('Draft discarded', { kind: 'success' });
      onUpdated?.({ coverLetter: null, coverLetterPrevious: null });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    }
  };

  const copyAll = async () => {
    const text = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast('Copied to clipboard', { kind: 'success' });
    } catch {
      // Fallback: select the textarea text (only mounted in edit mode)
      bodyRef.current?.select();
      toast('Select all → Copy', { kind: 'info' });
    }
  };

  // PDF export — A4, ~0.9" margins, Times. A right-aligned date, a bold
  // subject line, a muted company/location line, a hairline rule, then the
  // body set fully-justified (last line of each paragraph left ragged).
  // Filename: "Cover Letter - <Company> - <Role>.pdf". Client-side only.
  const downloadPdf = async () => {
    try {
      // T339 — through lazyImport, so a tab older than the last deploy gets
      // "reload and try again" instead of a 404'd chunk filename.
      const { jsPDF } = await lazyImport(() => import('jspdf'), 'The PDF exporter');
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 64; // ~0.9"
      const maxW = pageW - margin * 2;
      let y = margin;

      // Date — right aligned, muted
      doc.setFont('times', 'normal');
      doc.setFontSize(10.5);
      doc.setTextColor(110);
      doc.text(formatLetterDate(), pageW - margin, y, { align: 'right' });
      doc.setTextColor(20);
      y += 30;

      // Subject — bold, prominent
      doc.setFont('times', 'bold');
      doc.setFontSize(14);
      const subjectLines = doc.splitTextToSize(subject || 'Cover letter', maxW);
      doc.text(subjectLines, margin, y);
      y += subjectLines.length * 18;

      // Company · location — muted italic
      const metaParts = [
        job?.company,
        [job?.location?.city, job?.location?.country].filter(Boolean).join(', '),
      ].filter(Boolean);
      if (metaParts.length) {
        y += 4;
        doc.setFont('times', 'italic');
        doc.setFontSize(10.5);
        doc.setTextColor(110);
        doc.text(metaParts.join('   ·   '), margin, y);
        doc.setTextColor(20);
        y += 6;
      }

      // Hairline divider
      doc.setDrawColor(205);
      doc.line(margin, y, pageW - margin, y);
      y += 26;

      // Body — justified. Split into paragraphs (blank line) then honour any
      // single newlines inside a paragraph as hard breaks (signature block).
      doc.setFont('times', 'normal');
      doc.setFontSize(11.5);
      const lineHeight = 16.5;
      const paraGap = 9;
      const paragraphs = String(body || '').split(/\n\s*\n/);
      for (const para of paragraphs) {
        const segments = para.split('\n');
        segments.forEach((seg) => {
          const trimmed = seg.trim();
          if (!trimmed) {
            y += lineHeight; // blank segment → keep vertical rhythm
            return;
          }
          const lines = doc.splitTextToSize(trimmed, maxW);
          lines.forEach((line, i) => {
            if (y + lineHeight > pageH - margin) {
              doc.addPage();
              y = margin;
            }
            // Last wrapped line of a segment sits before a hard break, so
            // leave it ragged (don't justify).
            const flush = i === lines.length - 1;
            drawJustifiedLine(doc, line, margin, y, maxW, flush);
            y += lineHeight;
          });
        });
        y += paraGap;
      }

      // Filename: "Cover Letter - <Company> - <Role>.pdf". Keeps case and
      // spaces so it reads naturally when the recruiter saves it. Strips
      // only filesystem-illegal chars. Matches the tailored-CV helper.
      const safe = (s) =>
        String(s ?? '')
          .replace(/[\\/:*?"<>|\x00-\x1f]/g, '')
          .trim()
          .slice(0, 100);
      const filename =
        ['Cover Letter', safe(job?.company ?? ''), safe(job?.title ?? '')].filter(Boolean).join(' - ') + '.pdf';

      doc.save(filename);
      toast('PDF downloaded', { kind: 'success' });
    } catch (e) {
      // A stale build is not an export failure — nothing went wrong with the
      // letter, and the only useful thing to say carries a button.
      if (e?.stale) {
        toast(e.message, {
          kind: 'error',
          action: { label: 'Reload', onClick: () => window.location.reload() },
        });
        return;
      }
      toast(`PDF export failed: ${e.message}`, { kind: 'error' });
    }
  };

  const previewParagraphs = toParagraphs(body);
  const metaLine = [job?.location?.city, job?.location?.country].filter(Boolean).join(', ');

  return (
    <>
    {/* T131-fu — built on <Modal> (bare, size lg, scroll). The nested
        ConfirmDialogs render as SIBLINGS of Modal (below), and Modal's Escape
        is suppressed while one is open so a single Escape closes only it. */}
    <Modal
      role="dialog"
      size="lg"
      scroll
      labelledById="cover-letter-title"
      onClose={attemptClose}
      escapeClosesOnTop={!confirmDiscard && !confirmClose && !confirmRestore}
    >
        {/* Header — tightly bound to the job */}
        <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-brand-500">Cover letter</p>
            <h2 id="cover-letter-title" className="mt-0.5 truncate text-base font-semibold text-slate-100">
              {job?.title ?? 'Job'}
              <span className="ml-2 font-normal text-slate-400">@ {job?.company ?? '?'}</span>
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {job?.location?.city && `${job.location.city}, `}{job?.location?.country}
              {letter && (
                <>
                  {' · '}
                  generation {letter.generationCount}
                  {letter.generatedAt && ` · ${formatDateTime(letter.generatedAt)}`}
                  {letter.editedAt && ` · edited ${formatDateTime(letter.editedAt)}`}
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={attemptClose}
            aria-label="Close"
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
          >
            ✕
          </button>
        </header>

        {/* Output-language selector — governs generate + regenerate in this
            modal. Sits outside the scroll area so its dropdown isn't clipped. */}
        <div className="relative z-30 flex items-center gap-3 border-b border-slate-800 bg-slate-900/40 px-4 py-2.5">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">Language</span>
          <LanguageSelect value={language} onChange={changeLanguage} id="cover-letter-language" />
          {/* T469 — a default that changes by itself has to say why, or
              it reads as a bug. Only while it is still the default:
              once he picks, the reason is him. */}
          <span className="hidden text-xs text-slate-500 sm:inline">
            {language === langDefault.code
              ? languageReasonText(langDefault.reason, langDefault.code)
              : 'the letter will be written in this language'}
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-slate-950/30 p-4">
          {!letter ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                Generate a cover letter tied to <strong className="text-slate-200">{job?.title}</strong> at{' '}
                <strong className="text-slate-200">{job?.company}</strong>. Uses your full profile + this
                posting's description. Cost: {AI_COST.coverLetter.cost} per generation.
              </p>
              <button
                type="button"
                onClick={() => setShowNotesInput((v) => !v)}
                className="text-xs text-brand-500 underline decoration-dotted hover:text-brand-400"
              >
                {showNotesInput ? 'Hide' : 'Add'} notes for Claude (optional)
              </button>
              {showNotesInput && (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder='e.g. "Emphasise the GCP migration at Acme" or "I am relocating to Copenhagen by March"'
                  className="w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
                />
              )}
              {hasAnthropicKey ? (
                <button
                  type="button"
                  onClick={generate}
                  disabled={generating}
                  className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-500 disabled:opacity-50"
                >
                  {generating ? <AiBusy feature="coverLetter" compact /> : '✨ Generate cover letter'}
                </button>
              ) : (
                <AiKeyMissingNotice feature="Cover letters" />
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Mode toggle */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wide text-slate-500">
                  {mode === 'edit' ? 'Editing' : 'Preview'}
                </span>
                <button
                  type="button"
                  onClick={() => setMode((m) => (m === 'edit' ? 'preview' : 'edit'))}
                  className="rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
                >
                  {mode === 'edit' ? '👁 Preview' : '✎ Edit'}
                </button>
              </div>

              {mode === 'preview' ? (
                /* Typeset "paper" sheet — what the PDF will look like.
                   theme-exempt: the sheet is a fixed cream page on every theme
                   because it previews a PDF, so its ink and rules must stay
                   dark. Retargeting them to --c-text-1 would print near-white
                   on cream under the dark themes.
                   theme-exempt:begin */
                <article className="mx-auto w-full max-w-2xl rounded-lg bg-[#fbfaf6] px-8 py-10 font-serif text-slate-900 shadow-xl ring-1 ring-black/10 sm:px-12 sm:py-14">
                  <p className="text-right text-xs text-slate-500">{formatLetterDate()}</p>
                  <h3 className="mt-5 text-lg font-semibold leading-snug text-slate-900">
                    {subject || 'Cover letter'}
                  </h3>
                  <p className="mt-1 text-xs italic text-slate-500">
                    {job?.company}
                    {metaLine && `   ·   ${metaLine}`}
                  </p>
                  <hr className="my-5 border-slate-300" />
                  {previewParagraphs.length > 0 ? (
                    previewParagraphs.map((p, i) => (
                      <p
                        key={i}
                        className="mb-4 whitespace-pre-line text-justify text-[15px] leading-7 text-slate-800 last:mb-0 hyphens-auto"
                      >
                        {p}
                      </p>
                    ))
                  ) : (
                    <p className="text-sm italic text-slate-400">The letter body is empty — switch to Edit to write it.</p>
                  )}
                </article>
              ) /* theme-exempt:end */ : (
                <div className="space-y-4">
                  {/* Subject */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-slate-400">Subject</label>
                    <input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  {/* Body */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-slate-400">Letter body</label>
                    <textarea
                      ref={bodyRef}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={16}
                      className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm leading-relaxed text-slate-100 focus:border-brand-500 focus:outline-none"
                      spellCheck="true"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      {body.length} characters · ~{Math.round(body.split(/\s+/).filter(Boolean).length)} words
                    </p>
                  </div>
                </div>
              )}

              {/* Tips */}
              {letter.tips?.length > 0 && (
                <div className="rounded-lg border border-amber-700 bg-amber-950/20 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-amber-400">
                    ✨ Reminders for this application
                  </div>
                  <ul className="mt-2 space-y-1 text-xs text-amber-200">
                    {letter.tips.map((t, i) => (
                      <li key={i}>• {t}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Notes for next regeneration */}
              <details className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm">
                <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200">
                  Notes for regeneration (optional)
                </summary>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder='e.g. "Make it shorter, drop the second paragraph about Terraform"'
                  className="mt-2 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
                />
              </details>
            </div>
          )}
        </div>

        {/* T133 — previous-version chip (icon + text, never colour alone) */}
        {previous && (
          <div className="flex flex-wrap items-center gap-2 border-t border-amber-800/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
            <span aria-hidden="true">↩</span>
            <span>
              Previous version available
              {previous.generatedAt && ` · ${formatDateTime(previous.generatedAt)}`}
            </span>
            <button
              type="button"
              onClick={() => setConfirmRestore(true)}
              disabled={restoring}
              className="ml-auto rounded border border-amber-600 px-2 py-0.5 text-amber-200 transition hover:bg-amber-600/10 disabled:opacity-50"
            >
              Restore previous
            </button>
          </div>
        )}

        {/* Footer actions */}
        {letter && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
            {hasAnthropicKey && (
              <button
                type="button"
                onClick={generate}
                disabled={generating || saving}
                className="rounded-md border border-brand-600 px-3 py-1.5 text-xs text-brand-500 hover:bg-brand-600/10 disabled:opacity-50"
                title="Re-run with current notes — overwrites the body"
              >
                {generating ? <AiBusy feature="coverLetter" compact label="Regenerating" /> : '✨ Regenerate'}
              </button>
            )}
            <button
              type="button"
              onClick={saveEdits}
              disabled={!dirty || saving || generating}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
            >
              {saving ? 'saving…' : 'Save edits'}
            </button>
            <button
              type="button"
              onClick={copyAll}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            >
              📋 Copy
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
              title="Download as PDF (A4)"
            >
              📄 Download PDF
            </button>
            <button
              type="button"
              onClick={() => setConfirmDiscard(true)}
              className="ml-auto rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-500 hover:border-red-700 hover:text-red-400"
            >
              Discard
            </button>
          </footer>
        )}
    </Modal>

      {confirmDiscard && (
        <ConfirmDialog
          title="Discard this cover letter draft?"
          message="Re-generating is cheap, but any manual edits you made will be lost."
          confirmLabel="Discard"
          busyLabel="discarding…"
          onCancel={() => setConfirmDiscard(false)}
          onConfirm={async () => {
            await discard();
            setConfirmDiscard(false);
          }}
        />
      )}

      {confirmClose && (
        <ConfirmDialog
          title="Discard unsaved edits?"
          message="You've edited this letter but haven't saved. Closing now loses those changes."
          confirmLabel="Discard & close"
          cancelLabel="Keep editing"
          busyLabel="closing…"
          onCancel={() => setConfirmClose(false)}
          onConfirm={() => {
            setConfirmClose(false);
            onClose?.();
          }}
        />
      )}

      {confirmRestore && (
        <ConfirmDialog
          title="Restore the previous version?"
          message="Replaces the current draft with the version from before the last regeneration. The current one is discarded."
          confirmLabel="Restore previous"
          cancelLabel="Keep current"
          tone="default"
          busyLabel="restoring…"
          onCancel={() => setConfirmRestore(false)}
          onConfirm={restore}
        />
      )}
    </>
  );
}
